import React, { useState, useContext, useEffect } from "react";
import ResponsiveAppBar from "../../components/Navbar";
import { getInformation, getPricing, update, updatePricing } from "../../services/customer";
import { UserContext } from "../../context/UserProvider.jsx";
import Tab from "./tab.jsx";
import {
  Tabs,
  Tab as SectionTab,
  TextField,
  Grid,
  Container,
  Paper,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { getSalesPeople } from "../../services/admin";
import Select from "react-select";
import { isMobile } from "react-device-detect";
import LoadingScreen from "../../components/LoadingScreen";
import PricingEditor, {
  buildPricingRules,
  pricingRulesComplete,
} from "./PricingEditor";
import PaymentInformation from './PaymentInformation';
import { canManagePaymentOptions } from '../../services/customerPaymentOptions';
import ProductMarkupOverrides from "./ProductMarkupOverrides";

const App = () => {
  const [section, setSection] = useState(0);
  const [customerData, setCustomerData] = useState(null);
  const [customerCreatedDate, setCustomerCreatedDate] = useState(null);
  const [initUpdate, setInitUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricingCategories, setPricingCategories] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [pricingComplete, setPricingComplete] = useState(false);
  const [productOverrides, setProductOverrides] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const { token, access } = useContext(UserContext);
  const canReassignSalesPerson = access?.control?.["customer - sales person reassign"] === true;
  const { id } = useParams();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = (background = false) => {
    if (!background) setLoading(true);
    setLoadError("");
    getInformation(token, id).then((res) => {
      if (!res || res.status) throw new Error(res?.data?.message || "Customer information could not be loaded.");
      const customerFields = res.data ?? res;
      setCustomerData(customerFields);
      setCustomerCreatedDate(res.created_last ?? customerFields.created_last ?? null);
    }).catch((err) => setLoadError(err.message || "Customer information could not be loaded."))
      .finally(() => setLoading(false));

    setPricingLoading(true);
    getPricing(token, id).then((pricing) => {
      if (!pricing || pricing.status) throw new Error("Pricing could not be loaded. You can still edit customer information.");
      setPricingError("");
      setPricingCategories(pricing.categories ?? []);
      setPricingRules(buildPricingRules(pricing.categories ?? [], pricing.rules ?? []));
      setPricingComplete(pricing.pricing_complete === true);
      setProductOverrides(pricing.product_overrides ?? []);
    }).catch(() => setPricingError("Pricing could not be loaded. You can still edit customer information."))
      .finally(() => setPricingLoading(false));
  };

  const handleUpdate = (payload) => {
    setSaving(true);
    setSaveError("");

    let payloadData = {
      "company-name": payload.companyName,
      "account-number": payload.accountNumber,
      "address-line-1": payload.addressLine1,
      "address-line-2": payload.addressLine2,
      email: payload.emailAddress,
      phone: payload.phoneNumber,
      password: payload.password,
      "send-reminder": payload.sendReminder,
    };

    if (canReassignSalesPerson) {
      payloadData["sales-person"] = payload.salesPerson;
    }

    if (payload?.emailAddress.includes("-(disabled)")) {
      payloadData["token"] = payload?.emailAddress;
    }

    update(token, payloadData, id).then((res) => {
      if (res?.status) {
        const validationMessage = Object.values(res.data?.errors ?? {})
          .flat()
          .join(" ");
        setSaveError(
          validationMessage ||
            res.data?.message ||
            "Customer information could not be updated."
        );
        setSaving(false);
        return;
      }

      alert("Customer information updated successfully!");
      setSaving(false);
      loadData();
      setInitUpdate(false);
    }).catch(() => {
      setSaveError("Customer information could not be updated. Please try again.");
      setSaving(false);
    });
  };

  if (loadError && !customerData) {
    return (
      <>
        <ResponsiveAppBar />
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <Alert severity="error" role="alert">
            {loadError}
          </Alert>
        </Container>
      </>
    );
  }

  if (!customerData) {
    return null;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth={"xl"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <h1>Customer Information</h1>
          </Grid>
          <Grid item xs={12} md={8}>
            <Tab />
          </Grid>
        </Grid>
        <div
          style={{
            minHeight: "320px",
            width: "100%",
            backgroundColor: "#ffff",
            marginTop: "20px",
          }}
        >
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, boxSizing: "border-box" }}>
            {pricingError && <Alert severity="warning">{pricingError}</Alert>}
            <Tabs value={section} onChange={(_, value) => setSection(value)} variant="scrollable" scrollButtons="auto" aria-label="Customer details sections" sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              {["Customer Information", "Category Markup", "Product Markup", ...(canManagePaymentOptions(access) ? ["Payment Information"] : [])].map((label, index) => <SectionTab key={label} label={label} id={`customer-section-tab-${index}`} aria-controls={`customer-section-panel-${index}`} sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }} />)}
            </Tabs>
            <Box role="tabpanel" id="customer-section-panel-0" aria-labelledby="customer-section-tab-0" hidden={section !== 0}>
              {!initUpdate ? (
                <InformationView customerData={customerData} setInitUpdate={setInitUpdate} customerCreatedDate={customerCreatedDate} />
              ) : (
                <UpdateForm customerData={customerData} onSubmit={handleUpdate} setInitUpdate={setInitUpdate} saveError={saveError} saving={saving} />
              )}
            </Box>
            <Box role="tabpanel" id="customer-section-panel-1" aria-labelledby="customer-section-tab-1" hidden={section !== 1}>
              {pricingLoading ? <Typography role="status">Loading category markup…</Typography> : !pricingError && <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography component="h2" variant="h6">Category Markup</Typography>
                  <Chip size="small" variant="outlined" color={pricingComplete ? "success" : "warning"} label={pricingComplete ? "Pricing complete" : "Pricing incomplete"} />
                </Box>
                <CategoryPricingForm token={token} customerId={id} categories={pricingCategories} rules={pricingRules} onSaved={() => loadData(true)} />
              </>}
            </Box>
            <Box role="tabpanel" id="customer-section-panel-2" aria-labelledby="customer-section-tab-2" hidden={section !== 2}>
              {pricingLoading ? <Typography role="status">Loading product markup…</Typography> : !pricingError && <ProductMarkupOverrides customerId={id} initialOverrides={productOverrides} onChanged={() => loadData(true)} token={token} />}
            </Box>
            {section === 3 && canManagePaymentOptions(access) && <Box role="tabpanel" id="customer-section-panel-3" aria-labelledby="customer-section-tab-3"><PaymentInformation customerId={id} token={token} /></Box>}
          </Paper>
        </div>
      </Container>
    </>
  );
};

export const CategoryPricingForm = ({ token, customerId, categories, rules, onSaved }) => {
  const [draft, setDraft] = useState(() => buildPricingRules(categories, rules));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => { setDraft(buildPricingRules(categories, rules)); }, [categories, rules]);
  const submit = async (event) => {
    event.preventDefault();
    if (saving || !pricingRulesComplete(categories, draft)) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const response = await updatePricing(token, customerId, draft);
      if (!response || response.status) throw new Error(Object.values(response?.data?.errors ?? {}).flat().join(" ") || "Pricing could not be saved.");
      setSaved(true);
      onSaved();
    } catch (caught) { setError(caught.message || "Pricing could not be saved."); }
    finally { setSaving(false); }
  };
  return <Box component="form" onSubmit={submit} sx={{ mt: 2, minWidth: 0, width: "100%" }}>
    <PricingEditor categories={categories} rules={draft} onChange={(next) => { setDraft(next); setSaved(false); }} />
    {error && <Alert severity="error">{error}</Alert>}
    {saved && <Alert severity="success">Category pricing saved.</Alert>}
    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={saving || !pricingRulesComplete(categories, draft)}>{saving ? "Saving…" : "Save category pricing"}</Button>
  </Box>;
};

const InformationView = ({
  customerData,
  setInitUpdate,
  customerCreatedDate,
}) => {
  const formatDateAdded = () => {
    const rawDate = customerCreatedDate;

    if (!rawDate) {
      return "";
    }

    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  return (
    <div style={isMobile ? { overflow: "scroll" } : {}}>
      <table className="table-information">
        <tbody>
          <tr>
            <td className="caption">
              <strong>Sales Person: </strong>
            </td>
            <td>
              {customerData["sales-person"]
                ? customerData["sales-person"].label
                : ""}
            </td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Account Number: </strong>
            </td>
            <td>{customerData["account-number"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Company Name: </strong>
            </td>
            <td>{customerData["company-name"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Date Added: </strong>
            </td>
            <td>{formatDateAdded()}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Address Line 1: </strong>
            </td>
            <td>{customerData["address-line-1"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Address Line 2: </strong>
            </td>
            <td>{customerData["address-line-2"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Phone Number: </strong>
            </td>
            <td>{customerData["phone"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Email Address: </strong>
            </td>
            <td>{customerData["email"] ?? ""}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Send Order Reminder: </strong>
            </td>
            <td>
              {customerData["send-reminder"] == 0 ||
              !customerData["send-reminder"]
                ? "Do not send reminder"
                : `Send after ${customerData["send-reminder"]} days from the last date of order`}
            </td>
          </tr>
        </tbody>
      </table>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 3,
          pb: 1,
        }}
      >
        <Button
          type="button"
          variant="contained"
          color="primary"
          sx={{ minWidth: 112, minHeight: 44 }}
          onClick={() => setInitUpdate(true)}
        >
          Update
        </Button>
      </Box>
    </div>
  );
};

export const UpdateForm = ({
  customerData,
  onSubmit,
  setInitUpdate,
  saveError,
  saving,
}) => {
  const { token, access } = useContext(UserContext);
  const canReassignSalesPerson = access?.control?.["customer - sales person reassign"] === true;
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    companyName: customerData["company-name"],
    accountNumber: customerData["account-number"],
    addressLine1: customerData["address-line-1"],
    addressLine2: customerData["address-line-2"],
    emailAddress: customerData["email"],
    phoneNumber: customerData["phone"],
    password: "",
    salesPerson: customerData["sales-person"],
    sendReminder: customerData["send-reminder"] ?? 0,
  });
  const [salesPersonList, setSalesPersonList] = useState([]);

  useEffect(() => {
    if (!canReassignSalesPerson) return;
    getSalesPeople(token).then((res) => setSalesPersonList(res.collection ?? []));
  }, [canReassignSalesPerson, token]);

  const handleSelectChange = (event) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      salesPerson: event,
    }));
  };

  const handleGeneratePassword = () => {
    const randomPassword = Math.random().toString(36).slice(-8);
    setFormData((prevFormData) => ({
      ...prevFormData,
      password: randomPassword,
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleDisable = () => {
    let confirmation = window.confirm(
      "Are you sure you want to disable this customer?"
    );
    if (confirmation) {
      onSubmit({
        ...formData,
        emailAddress: `${formData.emailAddress}-(disabled)`,
        token: "",
      });
    }
  };

  const handleEnable = () => {
    let confirmation = window.confirm(
      "Are you sure you want to enable this customer?"
    );
    if (confirmation) {
      onSubmit({
        ...formData,
        emailAddress: formData.emailAddress.replace("-(disabled)", ""),
        token: "",
      });
    }
  };

  const isDisabled = formData.emailAddress.includes("-(disabled)");

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, boxSizing: "border-box" }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {canReassignSalesPerson && <Grid item xs={12}>
            <Select
              defaultValue={formData.salesPerson}
              required
              options={salesPersonList}
              onChange={handleSelectChange}
              placeholder="Sales Person *"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  background: "#fff",
                  borderColor: "#9e9e9e",
                  height: "55px",
                  boxShadow: state.isFocused ? null : null,
                }),

                valueContainer: (provided, state) => ({
                  ...provided,
                  height: "55px",
                  padding: "0 6px",
                }),

                input: (provided, state) => ({
                  ...provided,
                  margin: "0px",
                }),
                indicatorSeparator: (state) => ({
                  display: "none",
                }),
                indicatorsContainer: (provided, state) => ({
                  ...provided,
                  height: "55px",
                }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </Grid>}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Address Line 1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Email Address"
              name="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Send Order Reminder (Number of Days from Last Order date)"
              name="sendReminder"
              value={formData.sendReminder}
              onChange={handleChange}
              type="number"
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={handleTogglePasswordVisibility}
                      type="button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="button" variant="contained" onClick={handleGeneratePassword}>
              Generate Password
            </Button>
          </Grid>
          {saveError && (
            <Grid item xs={12}>
              <Alert severity="error" role="alert">
                {saveError}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              variant="contained"
              style={{ marginLeft: "10px" }}
              color="primary"
              disabled={saving}
              onClick={() => setInitUpdate(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              style={{ marginLeft: "10px" }}
              color="error"
              disabled={saving}
              onClick={() => (isDisabled ? handleEnable() : handleDisable())}
            >
              {isDisabled ? "Enable" : "Disable"} Customer
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default App;
